import { computed, Ref, ref } from 'vue';
import { isArray } from './type_guards';

// Class to manage getting navigation items
class NavItemFetcher {
  // Flag to track if the data has been fetched. We only need
  // one instances of the data, so we don't need to fetch it
  // multiple times.
  protected _fetched = false;

  // The nav data is a Ref value. It allows the computed property
  // below to be reactive when the data changes.
  protected _navData: Ref<unknown[]> = ref([]);

  // Getter for navData so that we don't accidentally mutate the
  // data.
  get navData() {
    return this._navData;
  }

  // Fetches the nav items.
  async getNavItems() {
    if (this._fetched) {
      return;
    }

    try {
      const menuItemsUrl = `${import.meta.env.VITE_APP_SSO_API_BASE_URL}/static/menu.json`;
      const resp = await fetch(menuItemsUrl);
      if (resp.status !== 200) {
        throw new Error('Error Fetching Data from API');
      }

      const data = await resp.json();
      if (!isArray(data)) {
        throw new Error('Invalid Response from API');
      }

      this._navData.value = data;
      this._fetched = true;
    } catch (e) {
      console.error('Error fetching nav items', e);
    }
  }
}

// Singleton of the above class so that we only have one instance
const fetcher = new NavItemFetcher();

// Computed property that simply returns the navData, making this
// data immutable.
export const navItems = computed(() => fetcher.navData.value);

// Function to get the navigation items. Uses the singleton so that
// we don't have to worry about multiple instances.
export async function getNavItems() {
  await fetcher.getNavItems();
}
